import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { concatMap, map, catchError, of, withLatestFrom } from "rxjs";
import { FinancingCompaniesClient } from "src/api-service";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import { FinancingCompaniesActions } from "./financing-companies.actions";
import { DataActions } from "../../../../application/data/data.actions";
import { normalizeError } from "../../../../app-store-error.types";

@Injectable()
export class FinancingCompaniesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly financingCompaniesClient = inject(FinancingCompaniesClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FinancingCompaniesActions.load),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([, selectedCompany]) =>
                this.financingCompaniesClient
                    .getAllAdmin(selectedCompany.id, 1, 1000)
                    .pipe(
                        map(response =>
                            FinancingCompaniesActions.loadSuccess({ financingCompanies: response.data ?? [] }),
                        ),
                        catchError(error =>
                            of(
                                FinancingCompaniesActions.loadFailure({
                                    error: normalizeError(error),
                                    toastKey: "ADMIN_PANEL.EDIT_FINANCING_COMPANY_SECTIONS.TOAST.LOAD_ERROR",
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    createFinancingCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FinancingCompaniesActions.createFinancingCompany),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request }, selectedCompany]) =>
                this.financingCompaniesClient.create(request, selectedCompany.id).pipe(
                    concatMap(() => [
                        FinancingCompaniesActions.createFinancingCompanySuccess({
                            toastKey: "ADMIN_PANEL.EDIT_FINANCING_COMPANY_SECTIONS.TOAST.CREATE_SUCCESS",
                        }),
                        FinancingCompaniesActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            FinancingCompaniesActions.createFinancingCompanyFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_FINANCING_COMPANY_SECTIONS.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateFinancingCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FinancingCompaniesActions.updateFinancingCompany),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request, id }, selectedCompany]) =>
                this.financingCompaniesClient.patch(id, request, selectedCompany.id).pipe(
                    concatMap(() => [
                        FinancingCompaniesActions.updateFinancingCompanySuccess({
                            toastKey: "ADMIN_PANEL.EDIT_FINANCING_COMPANY_SECTIONS.TOAST.UPDATE_SUCCESS",
                        }),
                        FinancingCompaniesActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            FinancingCompaniesActions.updateFinancingCompanyFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_FINANCING_COMPANY_SECTIONS.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteFinancingCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FinancingCompaniesActions.deleteFinancingCompany),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ id }, selectedCompany]) =>
                this.financingCompaniesClient.delete(id, selectedCompany.id).pipe(
                    concatMap(() => [
                        FinancingCompaniesActions.deleteFinancingCompanySuccess({
                            toastKey: "ADMIN_PANEL.EDIT_FINANCING_COMPANY_SECTIONS.TOAST.DELETE_SUCCESS",
                        }),
                        FinancingCompaniesActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            FinancingCompaniesActions.deleteFinancingCompanyFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN_PANEL.EDIT_FINANCING_COMPANY_SECTIONS.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
